// src/utils/quoteVocabularyDraft.js
// SEAM 6 VERIFIER: qfup-7d3e9a41-draft
//
// Pure helpers for the Settings → Follow-up options editor's UNSAVED draft. No
// React, no Firebase, no imports — functions/scripts/testQuoteFollowUp.js
// evaluates this file directly, so keep it dependency-free.
//
// Same shape as src/utils/pipelineStageDraft.js: the editor keeps a BASE (the
// saved lists + the revision the edits started from) separately from the live
// server document, so:
//   - a removed row is only MARKED (pendingDelete) until Save;
//   - "unsaved changes" is derived by comparing the draft to the base, so
//     undoing an edit clears it;
//   - Save sends the BASE revision, so a change saved elsewhere meanwhile is
//     refused by the server instead of silently overwritten.
//
// These caps mirror VOCAB_MAX_ITEMS / VOCAB_MAX_LENGTH in
// functions/utils/quoteSchema.js (the server enforces them; these only let the
// screen say so before calling). testQuoteFollowUp.js fails if they differ.
export const VOCAB_MAX_ITEMS = 30;
export const VOCAB_MAX_LENGTH = 60;

let tempSeq = 0;

// `original` is the SAVED value this row started as. A rename changes `value`
// and leaves `original` alone, which is the only way pendingChanges can tell a
// rename from an untouched row — and the only way the order comparison can
// still line a renamed row up against the base list.
export function draftRowsFrom(values) {
  return (values || []).map((v, i) => ({
    key: `saved_${i}_${v}`,
    value: v,
    original: v,
    isNew: false,
    pendingDelete: false,
  }));
}

export function newDraftRow(value = '') {
  tempSeq += 1;
  return { key: `new_${tempSeq}`, value, original: null, isNew: true, pendingDelete: false };
}

// The live rows, in order, trimmed — what actually gets saved.
export function saveInputFrom(rows) {
  return (rows || [])
    .filter((r) => !r.pendingDelete)
    .map((r) => String(r.value || '').trim());
}

// What the draft changes relative to the base list.
export function pendingChanges(baseValues, rows) {
  const base = baseValues || [];
  const live = (rows || []).filter((r) => !r.pendingDelete);
  const out = { added: [], deleted: [], renamed: [], reordered: false };

  for (const r of rows || []) {
    const v = String(r.value || '').trim();
    if (r.isNew) {
      if (!r.pendingDelete && v) out.added.push(v);
      continue;
    }
    if (r.pendingDelete) { out.deleted.push(r.original); continue; }
    // Compare against what this row was SAVED as, not against itself.
    if (v !== r.original) out.renamed.push({ from: r.original, to: v || '(blank)' });
  }

  // Order compares only the SAVED rows that survive, against their base order,
  // by ORIGINAL value — a renamed row is still the same row.
  const keptOriginals = live.filter((r) => !r.isNew).map((r) => r.original);
  const baseOrder = base.filter((v) => keptOriginals.includes(v));
  out.reordered = keptOriginals.join('|') !== baseOrder.join('|');
  return out;
}

export function hasPendingChanges(changes) {
  return !!changes && (changes.added.length > 0 || changes.deleted.length > 0
    || changes.renamed.length > 0 || changes.reordered);
}

export function summarizePendingChanges(changes, listLabel) {
  if (!changes) return [];
  const where = listLabel ? ` (${listLabel})` : '';
  const lines = [];
  for (const v of changes.added) lines.push(`Added "${v}"${where}`);
  for (const v of changes.deleted) lines.push(`Removed "${v}"${where}`);
  for (const r of changes.renamed) lines.push(`Renamed "${r.from}" to "${r.to}"${where}`);
  if (changes.reordered) lines.push(`Changed the order${where}`);
  return lines;
}

// Move a row up (-1) or down (+1) among the rows that are still live. Returns a
// NEW array; out-of-range moves are a no-op.
export function moveRow(rows, key, delta) {
  const list = [...(rows || [])];
  const i = list.findIndex((r) => r.key === key);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= list.length) return list;
  [list[i], list[j]] = [list[j], list[i]];
  return list;
}

// Client-side pre-flight mirroring the server's refusals, so the screen can say
// what is wrong before calling. Returns null when the list is fine.
export function validateDraft(rows, listLabel) {
  const values = saveInputFrom(rows);
  if (values.length === 0) return `${listLabel} needs at least one option.`;
  if (values.length > VOCAB_MAX_ITEMS) return `${listLabel} cannot have more than ${VOCAB_MAX_ITEMS} options.`;
  if (values.some((v) => !v)) return `${listLabel} cannot contain a blank option.`;
  const tooLong = values.find((v) => v.length > VOCAB_MAX_LENGTH);
  if (tooLong) return `"${tooLong.slice(0, 20)}…" is too long — ${listLabel} options are capped at ${VOCAB_MAX_LENGTH} characters.`;
  const seen = new Set();
  for (const v of values) {
    const k = v.toLowerCase();
    if (seen.has(k)) return `"${v}" appears twice in ${listLabel}.`;
    seen.add(k);
  }
  return null;
}

// The server refuses a save built on an out-of-date document with
// failed-precondition ("Someone else changed these lists…").
export function isStaleListError(err) {
  const code = String((err && err.code) || '');
  const msg = String((err && err.message) || '');
  return /failed-precondition/.test(code) && /someone else/i.test(msg);
}
